import { useEffect } from 'react';
import { useGetWorkspace } from '../serverState/workspace';
import { AnalyticsEvent, AnalyticsEventPayloads } from './analyticsEvent';
import mixpanel from 'mixpanel-browser';
import Workspace from '../types/Workspace';
import { resetSessionState } from './sessionState';
import { inslytic } from './inslytic';

export type AnalyticsPayload<E extends AnalyticsEvent> = AnalyticsEventPayloads[E];

let workspace: Workspace | null = null;

export const analytics = {
  initialize: () => {
    const apiHost = import.meta.env.VITE_MIXPANEL_API_HOST;
    mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
      debug: false,
      track_pageview: false,
      // A cookie on .taskonio.com is shared with the landing page, so a visitor keeps one
      // device ID from the first landing visit to signup.
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      ...(apiHost ? { api_host: apiHost } : {}),
    });
    inslytic.initialize();
  },

  track: <E extends AnalyticsEvent>(event: E, payload: AnalyticsPayload<E>): void => {
    let spaceAndOrgData = {};
    if (workspace && needsSpaceAndOrgId(event)) {
      // A spaceId/organizationId key the caller passes wins, whatever its value: the cached
      // workspace follows the last open space, which lags the URL after a space switch and has
      // nothing to do with org-level events. `spaceId: null` means the event has no space;
      // `organizationId: undefined` means unknown, which must not become another org's id.
      spaceAndOrgData = {
        spaceId: 'spaceId' in payload ? payload.spaceId : workspace.id,
        organizationId: 'organizationId' in payload ? payload.organizationId : workspace.organizationId,
      };
    }
    const enrichedPayload = { ...payload, ...spaceAndOrgData, accountId: mixpanel.get_distinct_id() as string };
    mixpanel.track(event, enrichedPayload);
    inslytic.track(event, enrichedPayload);
  },

  identify: (userId: string) => {
    mixpanel.identify(userId);
    inslytic.identify(userId);
  },

  setUserProperties: (properties: Record<string, any>) => {
    mixpanel.people.set(properties);
  },

  logout: () => {
    mixpanel.reset();
    inslytic.logout();
    // A re-login in this tab, by the same user or another, starts a new app session.
    resetSessionState();
  },
};

export const useAnalytics = () => {
  const workspaceQuery = useGetWorkspace();

  useEffect(() => {
    // Update workspace whenever workspaceQuery changes
    workspace = workspaceQuery?.data || null;
  }, [workspaceQuery]);

  return analytics;
};

const needsSpaceAndOrgId = (event: AnalyticsEvent): boolean => {
  if (
    event === AnalyticsEvent.taskCreated ||
    event === AnalyticsEvent.taskCompleted ||
    event === AnalyticsEvent.aiCompleted ||
    event === AnalyticsEvent.screenOpened ||
    event === AnalyticsEvent.sessionStarted
  ) {
    return true;
  }
  return false;
};
